import { Address, TupleReader, beginCell } from '@ton/core';
import { TonClient } from '@ton/ton';
import {
  CurveConfig,
  CurveState,
  readCurveParamsFromStack,
} from '@gong/curve-math';

import { API_URL, TON_API } from '../config';
import { curveStateFromData } from '../lib/phase';

export interface LoadedLaunch {
  curve: Address;
  state: CurveState;
  config: CurveConfig;
  minter: Address | null;
  jettonWallet: Address | null;
  deployer: Address | null;
  channel: Address | null;
  feeVault: Address | null;
  locker: Address | null;
  pendingFees: bigint;
}

export interface FactorySnapshot {
  admin: Address;
  launchCount: number;
  launchFee: bigint;
  paused: boolean;
  treasury: Address | null;
  feeVault: Address | null;
}

export interface FeeVaultSnapshot {
  protocol: Address | null;
  protocolAccrued: bigint | null;
  totalAccrued: bigint | null;
}

export interface ClaimableFees {
  recipient: Address;
  channelAccrued: bigint;
  claimable: bigint | null;
  isProtocolRecipient: boolean;
}

function client(): TonClient {
  return new TonClient({ endpoint: API_URL ? `${API_URL.replace(/\/$/, '')}/api/ton-rpc` : TON_API });
}

function unwrapStruct(stack: TupleReader): TupleReader {
  return stack.remaining === 1 ? stack.readTuple() : stack;
}

/**
 * Read BondingCurve getters. Quotes still run in curve-math; this only hydrates
 * the state those functions consume. Getter tuple layout matches CurveData and
 * CurveAddresses in contracts/contracts/messages.tact.
 */
export async function loadLaunch(curve: Address): Promise<LoadedLaunch> {
  const ton = client();
  const data = await ton.runMethod(curve, 'curveData');
  const addrs = await ton.runMethod(curve, 'curveAddresses');

  const d = unwrapStruct(data.stack);
  const phase = d.readNumber();
  const activatedAt = d.readNumber();
  const realTon = d.readBigNumber();
  const seedTon = d.readBigNumber();
  const excessSeedTon = d.readBigNumber();
  const antiSnipeTon = d.readBigNumber();
  const sold = d.readBigNumber();
  const seedJettons = d.readBigNumber();
  const devBuyJettons = d.readBigNumber();
  const pendingFees = d.readBigNumber();

  const a = unwrapStruct(addrs.stack);
  a.readAddress();
  const deployer = a.readAddress();
  const minter = a.readAddress();
  const jettonWallet = a.readAddress();
  const channel = a.readAddress();
  const feeVault = a.readAddress();
  const locker = a.readAddress();

  // Never quote a real trade using unrelated default curve parameters.
  const params = await ton.runMethod(curve, 'params');
  const config = parseCurveParams(unwrapStruct(params.stack));

  return {
    curve,
    state: curveStateFromData({
      phase,
      activatedAt,
      realTon,
      seedTon,
      excessSeedTon,
      antiSnipeTon,
      sold,
      seedJettons,
      devBuyJettons,
      pendingFees,
    }),
    config,
    minter,
    jettonWallet,
    deployer,
    channel,
    feeVault,
    locker,
    pendingFees,
  };
}

export async function loadLockerReadiness(locker: Address, curve: Address): Promise<boolean> {
  const result = await client().runMethod(locker, 'lockerData');
  const stack = unwrapStruct(result.stack);
  const ownerCurve = stack.readAddress();
  const locked = Boolean(stack.readNumber());
  const dexKind = stack.readNumber();
  const lpPrincipal = stack.readBigNumber();
  const kPrincipal = stack.readBigNumber();
  return Boolean(ownerCurve?.equals(curve) && locked && dexKind === 1 && lpPrincipal > 0n && kPrincipal > 0n);
}

function parseCurveParams(stack: TupleReader): CurveConfig {
  return readCurveParamsFromStack(() => stack.readBigNumber(), stack.remaining);
}

export async function loadFactory(factory: Address): Promise<FactorySnapshot> {
  const ton = client();
  const result = await ton.runMethod(factory, 'factoryData');
  const stack = unwrapStruct(result.stack);
  const admin = stack.readAddress();
  const treasury = stack.readAddress();
  const feeVault = stack.readAddress();
  const launchFee = stack.readBigNumber();
  const paused = Boolean(stack.readNumber());
  const launchCount = stack.readNumber();
  return { admin, launchFee, paused, treasury, feeVault, launchCount };
}

export async function loadFactoryParams(factory: Address): Promise<{ config: CurveConfig; fieldCount: 13 | 20 }> {
  const result = await client().runMethod(factory, 'params');
  const stack = unwrapStruct(result.stack);
  const fieldCount = stack.remaining;
  if (fieldCount !== 13 && fieldCount !== 20) throw new Error(`Неизвестный формат фабрики: ${fieldCount} полей.`);
  return { config: parseCurveParams(stack), fieldCount };
}

export async function loadFeeVault(vault: Address): Promise<FeeVaultSnapshot> {
  const ton = client();
  const [protocol, protocolAccrued, totalAccrued] = await Promise.all([
    ton.runMethod(vault, 'protocol'),
    ton.runMethod(vault, 'protocolAccrued').catch((error: unknown) => {
      if (error instanceof Error && /exit_code:\s*11\b/.test(error.message)) return null;
      throw error;
    }),
    ton.runMethod(vault, 'totalAccrued'),
  ]);
  return {
    protocol: protocol.stack.readAddress(),
    protocolAccrued: protocolAccrued?.stack.readBigNumber() ?? null,
    totalAccrued: totalAccrued.stack.readBigNumber(),
  };
}

/** The vault is shared by launches: claim withdraws the wallet's full accrued balance. */
export async function loadClaimableFees(vault: Address, channel: Address, wallet: Address): Promise<ClaimableFees> {
  const ton = client();
  const recipientResult = await ton.runMethod(vault, 'channelRecipient', [
    { type: 'slice', cell: beginCell().storeAddress(channel).endCell() },
  ]);
  const recipient = recipientResult.stack.readAddress();
  if (!recipient.equals(wallet)) {
    return { recipient, channelAccrued: 0n, claimable: 0n, isProtocolRecipient: false };
  }
  const [accruedResult, protocolResult] = await Promise.all([
    ton.runMethod(vault, 'accruedOf', [
      { type: 'slice', cell: beginCell().storeAddress(wallet).endCell() },
    ]),
    ton.runMethod(vault, 'protocol'),
  ]);
  const channelAccrued = accruedResult.stack.readBigNumber();
  const isProtocolRecipient = protocolResult.stack.readAddress().equals(wallet);
  // FeeVault.claim prioritizes the protocol pot when both roles use one wallet.
  const claimable = isProtocolRecipient
    ? await ton.runMethod(vault, 'protocolAccrued')
      .then((result) => result.stack.readBigNumber())
      .catch((error: unknown) => {
        if (error instanceof Error && /exit_code:\s*11\b/.test(error.message)) return null;
        throw error;
      })
    : channelAccrued;
  return { recipient, channelAccrued, claimable, isProtocolRecipient };
}

export async function loadJettonWallet(minter: Address, owner: Address): Promise<Address> {
  const ton = client();
  const result = await ton.runMethod(minter, 'get_wallet_address', [
    { type: 'slice', cell: beginCell().storeAddress(owner).endCell() },
  ]);
  return result.stack.readAddress();
}

export function parseFriendlyAddress(value: string): Address {
  return Address.parse(value.trim());
}
